import { MapProperty } from 'cadenza/map/map-property';
import { RoutingEvent } from 'cadenza/map/routing/routing-constants';
import { RoutingRequestService } from 'cadenza/map/routing/routing-request-service';
import { RoutingInteraction } from 'cadenza/map/interactions/routing-interaction';
import { context } from 'cadenza/map/context';
import { EventBus } from 'cadenza/utils/event-bus';
import { RoutingFeatureLayer } from 'cadenza/map/routing/layer/routing-feature-layer';
import { currentWindowMediaXsOnly, mediaXsOnly } from 'cadenza/utils/media';
import { setRoutingMobileModeLayout } from 'cadenza/map/routing/routing-responsive-layout';
import { on, unByKey } from 'cadenza/utils/event-util';
import { workbookMapIframeApi } from 'cadenza/map/custom/workbook/workbook-map-post-message-api';
import { dispatchSidebarStateChange, SidebarState } from 'cadenza/map/actions/sidebar-state';
import { disableToolbarButtons } from 'cadenza/map/toolbar/map-toolbar-utils';
import { CommonDrawInteractionGroup } from 'cadenza/map/interactions/common-draw-interaction-group';
import { AddLayerPolygonAvoidanceZoneInteraction } from 'cadenza/map/routing/layer-polygons-as-avoidance-zones/layer-polygons-as-avoidance-zones';

/**
 * Type for the options passed when starting the routing UI
 *
 * @typedef {object} RoutingOptions
 * @property {RoutingMode} routingMode - The tab to be activated when the UI is created
 * @property {MapWaypoint} startingMapWaypoint - The map waypoint to use to populate the first waypoint field
 * @property {MapWaypoint} destinationMapWaypoint - The map waypoint to use to populate the last waypoint field
 * @property {boolean} allowLocalize - If true, allow to use current location for waypoints
 * @property {string} routingProfile - Routing profile to use (e.g. DRIVING_CAR)
 * @property {boolean} isWorkbook - True if routing is started within a workbook, otherwise false
 */

/**
 * @typedef {object} MapWaypoint
 * @property {string} name - Indicates the hint name for the point.
 * @property {number[]} mapSrsCoordinate - Indicates the coordinate of the point based on the map projection.
 */

const ROUTING_TOOLBAR_BUTTON_SELECTORS = [
  '#map-action--routing-directions',
  '#map-action--routing-isochrones'
];

export const routingContext = {
  _routingActive: false,
  _eventBus: null,
  _routingFeatureLayer: null,

  /**
   * The event bus
   *
   * @return {EventBus}
   */
  getEventBus () {
    if (!this._eventBus) {
      this._eventBus = new EventBus('cadenza/routing/event-bus');
    }
    return this._eventBus;
  },

  getRoutingFeatureLayer (map) {
    if (!this._routingFeatureLayer) {
      this._routingFeatureLayer = new RoutingFeatureLayer(map, this.getEventBus());
    }
    return this._routingFeatureLayer;
  },

  isRoutingActive () {
    return this._routingActive;
  },

  startRouting (map, /** @type {RoutingOptions} */ routingOptions) {
    return this._createRoutingUi(map, routingOptions).then(routingUi => {
      this._initRouting(map);
      return routingUi;
    });
  },

  _createRoutingUi (map, /** @type {RoutingOptions} */ routingOptions) {
    return import(/* webpackChunkName: "routing-sidebar" */ 'cadenza/map/routing/cadenza-routing-sidebar')
      .then(({ createRoutingUIRoot }) => createRoutingUIRoot(
        map,
        routingOptions,
        this._eventBus,
        new RoutingRequestService()
      ));
  },

  _initRouting (map) {
    const previousPrimaryInteraction = context(map).getCurrentPrimaryInteractionIfResettable();

    // stop routing when primary map interaction changes to another non-routing one
    const mapListenerKey = map.on(`change:${MapProperty.PRIMARY_INTERACTION}`, () => {
      const currentMapInteraction = map.get(MapProperty.PRIMARY_INTERACTION);

      const isRoutingInteraction = currentMapInteraction instanceof RoutingInteraction
        || currentMapInteraction instanceof CommonDrawInteractionGroup // drawing avoidance zones
        || currentMapInteraction instanceof AddLayerPolygonAvoidanceZoneInteraction; // add avoidance zones from map layers

      if (!isRoutingInteraction && this.isRoutingActive()) {
        map.un(mapListenerKey.type, mapListenerKey.listener);
        this._eventBus.dispatchEvent(RoutingEvent.ROUTING_STOPPED);
      }
    });

    if (currentWindowMediaXsOnly.matches) {
      setRoutingMobileModeLayout(true, map);
    }

    const workbookIframeApi = map.get(MapProperty.WORKBOOK_ID_AND_HASH) ? workbookMapIframeApi : undefined;
    const eventKeys = [
      this._eventBus.on(RoutingEvent.ROUTING_STARTED, () => {
        this._routingActive = true;
        if (workbookIframeApi) {
          disableToolbarButtons(true, { nonDisableableButtonSelectors: ROUTING_TOOLBAR_BUTTON_SELECTORS });
        }
        dispatchSidebarStateChange(workbookIframeApi, SidebarState.OPEN);
      }),
      this._eventBus.on(RoutingEvent.ROUTING_STOPPED, () => {
        unByKey(eventKeys);
        setRoutingMobileModeLayout(false, map);
        this._routingActive = false;
        if (workbookIframeApi) {
          disableToolbarButtons(false, { nonDisableableButtonSelectors: ROUTING_TOOLBAR_BUTTON_SELECTORS });
        }
        dispatchSidebarStateChange(workbookIframeApi, SidebarState.CLOSED);

        // restore previous primary interaction
        if (map.get(MapProperty.PRIMARY_INTERACTION) instanceof RoutingInteraction) {
          map.set(MapProperty.PRIMARY_INTERACTION, previousPrimaryInteraction);
        }
      }),
      on(mediaXsOnly, 'change', event => setRoutingMobileModeLayout(event.matches, map))
    ];
  }
};
